"use client";
import React, { useRef, useState, useEffect } from "react";
import { Section } from "@/components/ui/Section";
import { env } from "@/lib/environment";
import Image from "next/image";
import { Typography } from "@/components/ui/Typography";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";

// Simple chevron icons as SVG components
const ChevronLeftIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const imgBaseUrl = `${env.NEXT_PUBLIC_CDN_URL}/images/industries/retail-digital-signage-software`;

// Customer testimonial data
const customerTestimonials = [
  {
    id: 1,
    name: "Michael T",
    title: "Co Founder",
    company: "Electrical/Electronic Manufacturing",
    testimonial:
      "We needed to have video running on android displays in different countries. So, we used the Pickcel's digital signage software. I liked the report function that lets me see for how long the stores have the video running. And the support I received was world-class.",
  },
  {
    id: 2,
    name: "Tarun C",
    title: "Assistant Manager",
    company: "Reserve Bank of India",
    testimonial:
      "The overall experience with Pickcel has been great. The software makes it so simple to control and administer content remotely.",
  },
  {
    id: 3,
    name: "Shelli C",
    title: "Owner",
    company: "CARTER'S SUGAR SHOP",
    testimonial:
      "The Pickcel digital signage application is so user-friendly. Our menu changes quarterly, and the software allows me to schedule the menu ahead of time. So, when the holiday seasons are overwhelming, its one less thing I need to worry about.",
  },
  {
    id: 4,
    name: "Umesh S",
    title: "Partner",
    company: "Arogya Ahaara",
    testimonial:
      "What I liked about Pickcel is that the setup & configuration of the solution is super easy, and doesn't require any assistance.",
  },
  {
    id: 5,
    name: "Varun B",
    title: "Manager",
    company: "Sight and sound india",
    testimonial:
      "Pickcel has the best support team. Easiest digital signage software ever, no bugs.",
  },
  {
    id: 6,
    name: "Fabrice M",
    title: "AV/IT MANAGER",
    company: "ACCENT MEDIA SA",
    testimonial:
      "Pickcel's user interface is simple, intuitive, and easy for any type of user.",
  },
  {
    id: 7,
    name: "Carmel J",
    title: "HR Operations",
    company: "Cultgear",
    testimonial:
      "The Pickcel user interface is super comfortable and easy to adapt to. We are finding the solution to be exceedingly effective in making announcements and communications.",
  },
  {
    id: 8,
    name: "Frank O",
    title: "Product Manager",
    company: "Tezza Business Solutions",
    testimonial:
      "Pickcel came to me at a time when I was abandoned by another vendor with poor customer service. The response time from the Pickcel team was amazing. The user experience is top-notch with tons of FREE apps! Easily the best digital signage brand.",
  },
  {
    id: 9,
    name: "Saleem S",
    title: "IT - Manager",
    company: "FadenSpectrum",
    testimonial:
      "Firstly, the Pickcel application is very user-friendly. On request, the team can custom-design apps for you. They also have a cool option called Quickplay that makes the content release super fast.",
  },
  {
    id: 10,
    name: "Vishal R",
    title: "Business Head",
    company: "Engineer. AI",
    testimonial:
      "The experience of using the Pickcel software for our screens has been fantastic. The support is excellent!",
  },
  {
    id: 11,
    name: "Akhil V",
    title: "Product Manager",
    company: "Sopetel Technologies Private Limited",
    testimonial:
      "We have integrated Pickcel with one of our own solutions to manage our digital inventory. The software has almost all the features required for managing the cloud contents and it is very easy to use. Their customer support is excellent.",
  },
];

export default function CustomerVoices() {
  const swiperRef = useRef<any>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isSwiperReady, setIsSwiperReady] = useState(false);

  const goToPrev = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    }
  };

  const goToNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent, action: () => void) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  // Initialize current slide when swiper is ready
  useEffect(() => {
    if (isSwiperReady && swiperRef.current) {
      setCurrentSlide(swiperRef.current.realIndex || 0);
    }
  }, [isSwiperReady]);

  return (
    <Section
      variant="default"
      style={{
        backgroundImage: `url(${imgBaseUrl + "/bg.lZjHGtVoOeE1.jpg"})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-[40%_60%] gap-8 lg:gap-12 items-center ">
        {/* Left Side - Static Images */}
        <div className="relative hidden lg:block">
          <div className="z-1 p-6 relative">
            <Image
              src={imgBaseUrl + "/customer-img.2FA6-ShPFdZe.webp"}
              alt="Customer Voices"
              title="Customer Voices"
              width={500}
              height={500}
              className="w-full h-full object-cover grayscale"
            />
          </div>

          {/* Dots indicator */}
          <div className="absolute top-[-50px] right-[-40px] w-32 z-0">
            <Image
              src={imgBaseUrl + "/shape_163.wu1lX7dAqEXC.svg"}
              alt="Customer Voices"
              title="Customer Voices"
              width={500}
              height={500}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="absolute bottom-[10%] right-[20%] w-16 z-10">
            <Image
              src={imgBaseUrl + "/shape_165.QVzjdAIZxzQQ.svg"}
              alt="Customer Voices"
              title="Customer Voices"
              width={500}
              height={500}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] w-20 xl:w-44 z-10">
            <Image
              src={imgBaseUrl + "/smile-loading-animation.sFQt2QoZCgbI.gif"}
              alt="Customer Voices"
              title="Customer Voices"
              width={500}
              height={500}
              unoptimized
              className="w-full h-full object-cover mix-blend-multiply"
            />
          </div>
        </div>

        {/* Right Side - Swiper Slider */}
        <div className="relative">
          <Typography variant="h2" as="h2" className="mb-8">Customer Voices</Typography>
          <div className="relative">
            <Swiper
              spaceBetween={0}
              loop={true}
              slidesPerView={1}
              onSwiper={swiper => {
                swiperRef.current = swiper;
              }}
              onSlideChange={swiper => {
                setCurrentSlide(swiper.realIndex);
              }}
              className="customer-voices-swiper"
              role="region"
              aria-label="Customer testimonials carousel"
            >
              {customerTestimonials.map((testimonial, index) => (
                <SwiperSlide key={testimonial.id}>
                  <div 
                    className="space-y-6"
                    role="tabpanel"
                    aria-label={`Testimonial ${index + 1} of ${customerTestimonials.length}`}
                    aria-live="polite"
                  >
                    {/* Large quotation mark */}
                    <div className="text-8xl text-gray-300 font-serif leading-none">
                      <svg
                        width="28"
                        height="25"
                        viewBox="0 0 28 25"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M7.07692 0L10.0769 2.16028C9.5641 2.82894 8.94872 3.85764 8.23077 5.24639C7.51282 6.58371 6.89744 8.07533 6.38462 9.72126C5.8718 11.3157 5.64103 12.8588 5.69231 14.3504C6.05128 14.2476 6.33333 14.1961 6.53846 14.1961C6.79487 14.1447 7.05128 14.119 7.30769 14.119C8.69231 14.119 9.92308 14.5304 11 15.3534C12.0769 16.1249 12.6154 17.4108 12.6154 19.2111C12.6154 20.7541 12.1026 22.1171 11.0769 23.3001C10.1026 24.4317 8.76923 24.9975 7.07692 24.9975C4.71795 25.0489 2.94872 24.3031 1.76923 22.7601C0.589744 21.1656 0 19.2111 0 16.8965C0 15.2505 0.25641 13.5017 0.769231 11.6501C1.28205 9.79841 2.05128 7.8953 3.07692 5.94077C4.15385 3.93479 5.48718 1.95454 7.07692 0ZM22.4615 0L25.4615 2.16028C24.9487 2.82894 24.3333 3.85764 23.6154 5.24639C22.8974 6.58371 22.2821 8.07533 21.7692 9.72126C21.2564 11.3157 21.0256 12.8588 21.0769 14.3504C21.4359 14.2476 21.7179 14.1961 21.9231 14.1961C22.1795 14.1447 22.4359 14.119 22.6923 14.119C24.0769 14.119 25.3077 14.5304 26.3846 15.3534C27.4615 16.1249 28 17.4108 28 19.2111C28 20.7541 27.4872 22.1171 26.4615 23.3001C25.4872 24.4317 24.1538 24.9975 22.4615 24.9975C20.1026 25.0489 18.3333 24.3031 17.1538 22.7601C15.9744 21.1656 15.3846 19.2111 15.3846 16.8965C15.3846 15.2505 15.641 13.5017 16.1538 11.6501C16.6667 9.79841 17.4359 7.8953 18.4615 5.94077C19.5385 3.93479 20.8718 1.95454 22.4615 0Z"
                          fill="#131313"
                        ></path>
                      </svg>
                    </div>

                    {/* Testimonial text */}
                   

                    <Typography variant="body" as="div" className="leading-relaxed">{testimonial.testimonial}</Typography>

                    {/* Customer info */}
                    <div className="space-y-1">
                      <Typography variant="body" as="p" className="text-black">{testimonial.name}</Typography>
                      <Typography variant="body" as="div" className="text-gray-700">{testimonial.title}</Typography>
                      <Typography variant="body" as="p" className="text-gray-700">{testimonial.company}</Typography>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Navigation controls */}
            <div className="flex items-center justify-between mt-8" role="toolbar" aria-label="Testimonial navigation">
              <div 
                className="text-gray-300 text-4xl"
                aria-live="polite"
                aria-label={`Currently showing testimonial ${currentSlide + 1} of ${customerTestimonials.length}`}
              >
                {`${currentSlide + 1} / ${customerTestimonials.length}`}
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={goToPrev}
                  onKeyDown={(e) => handleKeyDown(e, goToPrev)}
                  className="p-2 text-gray-400 hover:text-black transition-colors"
                  aria-label="Previous testimonial"
                  type="button"
                  tabIndex={0}
                >
                  <ChevronLeftIcon className="w-6 h-6" />
                </button>
                <button
                  onClick={goToNext}
                  onKeyDown={(e) => handleKeyDown(e, goToNext)}
                  className="p-2 text-gray-400 hover:text-black transition-colors"
                  aria-label="Next testimonial"
                  type="button"
                  tabIndex={0}
                >
                  <ChevronRightIcon className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}
